import React, { Component } from "react"
import styled from "styled-components"
import get from "lodash.get"
import { connect } from "react-redux"
import { uncommentPost } from "./redux/reducers"
import UserAvatar from "../../common/UserAvatar"
import { formatAgo, formatAgoCompact } from "../../common/util/dates"

// One reply row on the shared thread grid (postArea/cp-postdetail.jsx):
// 96px rail with the avatar centered on the spine, content edge at 96.
// Header baseline: name · OP · time · #index right · ✕ delete (own/admin).
const RAIL = 96

const Row = styled.div`
  display: grid;
  grid-template-columns: ${RAIL}px 1fr;
  padding: 14px 20px 14px 0;
  border-bottom: ${p =>
    p.last ? "none" : "1px dashed rgba(190, 38, 252, 0.18)"};

  .del {
    opacity: 0.55;
    transition: opacity 120ms ease;
  }

  &:hover .del {
    opacity: 1;
  }

  @media (max-width: 600px) {
    grid-template-columns: 88px minmax(0, 1fr);
    padding: 12px 10px 12px 0;
  }
`

const RailCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 600px) {
    img {
      width: 64px !important;
      height: 64px !important;
    }
  }
`

const Main = styled.div`
  min-width: 0;
`

const Baseline = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 600px) {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 5px 8px;
  }
`

const Name = styled.span`
  font-family: "VT323", monospace;
  font-size: 17px;
  color: #00e7ff;
  line-height: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`

const OpPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border: 1px solid currentColor;
  font-family: "VT323", monospace;
  font-size: 12px;
  letter-spacing: 0.12em;
  color: #fc26fa;
  background: rgba(252, 38, 250, 0.06);
`

const Time = styled.span`
  font-family: "VT323", monospace;
  font-size: 13px;
  color: #3a4670;
  text-transform: uppercase;
`

const FullTime = styled(Time)`
  @media (max-width: 600px) {
    display: none;
  }
`

const CompactTime = styled(Time)`
  display: none;

  @media (max-width: 600px) {
    display: inline;
  }
`

const Index = styled.span`
  margin-left: auto;
  font-family: "VT323", monospace;
  font-size: 12px;
  color: #3a4670;

  @media (max-width: 600px) {
    margin-left: 0;
  }
`

const DeleteBtn = styled.button`
  background: transparent;
  border: 1px solid #ff001c;
  color: #ff001c;
  font-family: "VT323", monospace;
  font-size: 11px;
  padding: 1px 7px;
  cursor: pointer;
  line-height: 1.4;

  @media (max-width: 600px) {
    min-width: 44px;
    min-height: 44px;
    margin-left: auto;
  }

  &:hover {
    background: rgba(255, 0, 28, 0.12);
    box-shadow: 0 0 8px rgba(255, 0, 28, 0.3);
  }
`

const ReplyText = styled.div`
  font-size: 15.5px;
  color: #d6e1ff;
  line-height: 1.5;
  margin-top: 5px;
  word-break: break-word;
`

class Comment extends Component {
  delete = comment => {
    const { userId, postId } = this.props
    this.props.uncommentPost(userId, postId, comment)
  }

  render() {
    const { comment, userId, isAdmin, index = 0, isOp, last } = this.props
    // Same rule as posts: only the comment's author or an admin can
    // delete, so nobody else gets a button that would just 403.
    const commenterId = get(comment, "postedBy._id")
    const canDelete =
      !!userId &&
      (isAdmin || (commenterId && String(commenterId) === String(userId)))

    return (
      <Row last={last}>
        <RailCell>
          <UserAvatar user={comment.postedBy} size={72} square />
        </RailCell>
        <Main>
          <Baseline>
            <Name>{get(comment, "postedBy.name", "deletedUser")}</Name>
            {isOp && <OpPill>OP</OpPill>}
            {comment.created && (
              <React.Fragment>
                <FullTime>{formatAgo(comment.created)}</FullTime>
                <CompactTime>{formatAgoCompact(comment.created)}</CompactTime>
              </React.Fragment>
            )}
            <Index>#{String(index + 1).padStart(2, "0")}</Index>
            {canDelete && (
              <DeleteBtn
                className="del"
                onClick={() => this.delete(comment)}
                title="delete reply"
              >
                ✕
              </DeleteBtn>
            )}
          </Baseline>
          <ReplyText>{comment.text}</ReplyText>
        </Main>
      </Row>
    )
  }
}

export default connect(
  null,
  { uncommentPost }
)(Comment)
